/**
 * DashboardPage — KPI overview at /a/arche_fias_leads/. Open pipeline
 * value, deals by status, deals by stage, recent activity count.
 */

import { useFiasLeadsDashboard, useFiasLeadsDealRisk } from '../hooks/useFiasLeads';
import { SkeletonBlock } from '../components/Skeletons';
import { Link } from 'react-router-dom';
import { ARCHE_PATH } from '../constants';

interface Props {
  holderId: string;
}

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700',
  won: 'bg-emerald-100 text-emerald-700',
  lost: 'bg-rose-100 text-rose-700',
};

export function DashboardPage({ holderId }: Props) {
  const dashQuery = useFiasLeadsDashboard(holderId);
  const riskQuery = useFiasLeadsDealRisk(holderId);

  if (dashQuery.isLoading) return <SkeletonBlock lines={6} />;

  const d = dashQuery.data;
  const highRiskDeals = (riskQuery.data ?? []).filter((deal) => deal.risk_level === 'high');

  return (
    <div className="p-6 max-w-6xl mx-auto flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Pipeline at a glance.</p>
      </header>

      {!d ? (
        <p className="text-sm text-muted-foreground">No data yet.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4">
            <KpiCard
              label="Open pipeline value"
              value={`$${Number(d.openPipelineValue).toLocaleString()}`}
            />
            <KpiCard
              label="Open deals"
              value={d.dealsByStatus.find((s) => s.status === 'open')?.count ?? '0'}
            />
            <KpiCard label="Recent activity (7d)" value={d.recentActivityCount} />
          </div>

          <div className="rounded border border-border bg-card p-4">
            <h2 className="text-sm font-medium text-muted-foreground mb-3">Deals by status</h2>
            <div className="flex flex-wrap gap-2">
              {d.dealsByStatus.map((row) => (
                <div
                  key={row.status}
                  className={`rounded px-3 py-2 text-sm ${STATUS_COLORS[row.status] ?? 'bg-muted/40'}`}
                >
                  <span className="uppercase mr-2">{row.status}</span>
                  <span className="font-medium">{row.count}</span>{' '}
                  <span className="text-xs">(${Number(row.total_value).toLocaleString()})</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded border border-border bg-card p-4">
            <h2 className="text-sm font-medium text-muted-foreground mb-3">Deals by stage</h2>
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="py-2">Stage</th>
                  <th className="py-2">Deals</th>
                  <th className="py-2">Total value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {d.dealsByStage.map((s) => (
                  <tr key={s.stage_id}>
                    <td className="py-2">{s.stage_name}</td>
                    <td className="py-2">{s.deal_count}</td>
                    <td className="py-2">${Number(s.total_value).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {highRiskDeals.length > 0 && (
            <div className="rounded border border-rose-300 bg-rose-50 p-4">
              <h2 className="text-sm font-medium text-rose-900 mb-3">
                {highRiskDeals.length} high-risk deal{highRiskDeals.length === 1 ? '' : 's'}
              </h2>
              <ul className="divide-y divide-rose-200">
                {highRiskDeals.slice(0, 5).map((deal) => (
                  <li key={deal.id} className="py-2 flex items-center gap-3 text-sm">
                    <Link
                      to={`${ARCHE_PATH}/deals/${deal.id}`}
                      className="font-medium hover:underline text-rose-900"
                    >
                      {deal.name}
                    </Link>
                    <span className="text-xs text-rose-700">{deal.risk_flags.join(', ')}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold mt-1">{value}</p>
    </div>
  );
}
